import { useEffect, useState } from 'react';
import {
  ArrowRight, Check, Users, FileQuestion, BarChart3, Shield,
  Zap, Star, Link2, Mail, Menu, X, Heart, Globe, Flag
} from 'lucide-react';

type InfoTopic = 'about' | 'contact' | 'faq' | 'blog' | 'help';
type LegalTopic = 'privacy' | 'terms' | 'cookies' | 'acceptable-use' | 'disclaimer' | 'copyright' | 'gdpr' | 'data-retention' | 'dpa';

type Props = {
  onGetStarted: () => void;
  onNavigateInfo: (page: InfoTopic) => void;
  onNavigateLegal: (topic: LegalTopic) => void;
  onNavigateAdmin: () => void;
  onManageSubscription: () => void;
};

const FEATURES = [
  {
    icon: Link2,
    title: 'Unique Application Link',
    description: 'Each agency gets a branded link — share it on Facebook, LinkedIn, Indeed, or a QR code.',
  },
  {
    icon: FileQuestion,
    title: 'Custom Screening Questions',
    description: 'Build a form in minutes. Yes/No, multiple choice, short text, long text — all draggable to reorder.',
  },
  {
    icon: Users,
    title: 'Applicant Dashboard',
    description: 'All candidates in one place with status tracking, search, filtering, and CV downloads.',
  },
  {
    icon: BarChart3,
    title: 'Pipeline Management',
    description: 'Move candidates through New, Reviewing, Interview, and Hired with one click.',
  },
  {
    icon: Shield,
    title: 'GDPR-Compliant & Secure',
    description: "Every agency's data is completely isolated. Bank-level encryption on all files.",
  },
  {
    icon: Zap,
    title: 'Email Notifications',
    description: 'Instant alerts when a new application arrives. Automatic confirmation to candidates.',
  },
];

const TESTIMONIALS = [
  {
    quote: "We went from drowning in emails to a proper pipeline in one afternoon. Brilliant.",
    author: "Sarah K.",
    role: "Agency Owner",
    photo: "https://images.pexels.com/photos/5327585/pexels-photo-5327585.jpeg?auto=compress&cs=tinysrgb&w=80&h=80&fit=crop&crop=face",
  },
  {
    quote: "The application link works everywhere — Indeed, Facebook, even our email footers.",
    author: "James M.",
    role: "Agency Owner",
    photo: "https://images.pexels.com/photos/5490276/pexels-photo-5490276.jpeg?auto=compress&cs=tinysrgb&w=80&h=80&fit=crop&crop=face",
  },
  {
    quote: "Finally — a tool built for care agencies, not generic HR departments.",
    author: "Lisa T.",
    role: "Agency Owner",
    photo: "https://images.pexels.com/photos/5327921/pexels-photo-5327921.jpeg?auto=compress&cs=tinysrgb&w=80&h=80&fit=crop&crop=face",
  },
  {
    quote: "Set up our application page in under 10 minutes. The candidates love how smooth it is.",
    author: "David R.",
    role: "Agency Owner",
    photo: "https://images.pexels.com/photos/5490235/pexels-photo-5490235.jpeg?auto=compress&cs=tinysrgb&w=80&h=80&fit=crop&crop=face",
  },
  {
    quote: "I used to spend hours chasing CVs. Now everything just shows up in the dashboard.",
    author: "Jennifer W.",
    role: "Agency Owner",
    photo: "https://images.pexels.com/photos/7447847/pexels-photo-7447847.jpeg?auto=compress&cs=tinysrgb&w=80&h=80&fit=crop&crop=face",
  },
  {
    quote: "Hiring six care workers last month felt effortless. We had qualified candidates same day.",
    author: "Michael T.",
    role: "Director, Premier Home Health",
    photo: "https://images.pexels.com/photos/6148101/pexels-photo-6148101.jpeg?auto=compress&cs=tinysrgb&w=80&h=80&fit=crop&crop=face",
  },
];

function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
}

export default function LandingPage({ onGetStarted, onNavigateInfo, onNavigateLegal, onNavigateAdmin, onManageSubscription }: Props) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyEmail = () => {
    navigator.clipboard.writeText('carehire@outlook.com').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-sm border-b border-ink-200'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="font-bold text-ink-900 tracking-tight">CareHire</span>
          </div>

          <nav className="hidden md:flex items-center gap-6">
            <button onClick={() => scrollTo('pricing')} className="text-sm text-ink-500 hover:text-ink-900 transition-colors">Pricing</button>
            <button onClick={() => scrollTo('reviews')} className="text-sm text-ink-500 hover:text-ink-900 transition-colors">Reviews</button>
            <button onClick={() => onNavigateInfo('about')} className="text-sm text-ink-500 hover:text-ink-900 transition-colors">About</button>
            <button onClick={() => onNavigateInfo('faq')} className="text-sm text-ink-500 hover:text-ink-900 transition-colors">FAQ</button>
            <button onClick={() => onNavigateInfo('blog')} className="text-sm text-ink-500 hover:text-ink-900 transition-colors">Blog</button>
            <button onClick={onManageSubscription} className="text-sm text-ink-900 font-medium hover:text-ink-600 transition-colors">Manage Subscriptions</button>
            <button onClick={() => setReportOpen(true)} className="text-sm text-ink-500 hover:text-ink-900 transition-colors flex items-center gap-1.5">
              <Flag className="w-3.5 h-3.5" />
              Report a Problem
            </button>
          </nav>

          <div className="flex items-center gap-4">
            <button
              onClick={onManageSubscription}
              className="hidden sm:block text-sm text-ink-500 hover:text-ink-900 transition-colors"
            >
              Sign in
            </button>
            <button
              onClick={onGetStarted}
              className="hidden sm:flex items-center gap-1.5 px-4 py-2 bg-ink-900 hover:bg-ink-800 text-white text-sm font-medium rounded-md transition-colors"
            >
              Get started
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center text-ink-700"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-ink-200 px-4 py-4 space-y-1 animate-fade-in-down">
            <button onClick={() => { scrollTo('pricing'); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2.5 text-sm text-ink-600 hover:bg-ink-50 rounded-md transition-colors">Pricing</button>
            <button onClick={() => { scrollTo('reviews'); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2.5 text-sm text-ink-600 hover:bg-ink-50 rounded-md transition-colors">Reviews</button>
            <button onClick={() => { onNavigateInfo('about'); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2.5 text-sm text-ink-600 hover:bg-ink-50 rounded-md transition-colors">About</button>
            <button onClick={() => { onNavigateInfo('faq'); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2.5 text-sm text-ink-600 hover:bg-ink-50 rounded-md transition-colors">FAQ</button>
            <button onClick={() => { onNavigateInfo('blog'); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2.5 text-sm text-ink-600 hover:bg-ink-50 rounded-md transition-colors">Blog</button>
            <button onClick={() => { onManageSubscription(); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2.5 text-sm text-ink-900 font-medium hover:bg-ink-50 rounded-md transition-colors">Manage Subscriptions</button>
            <button onClick={() => { setReportOpen(true); setMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2.5 text-sm text-ink-600 hover:bg-ink-50 rounded-md transition-colors flex items-center gap-1.5">
              <Flag className="w-3.5 h-3.5" />
              Report a Problem
            </button>
            <button onClick={() => { onGetStarted(); setMobileMenuOpen(false); }} className="w-full flex items-center justify-center gap-1.5 px-5 py-3 mt-2 bg-ink-900 text-white text-sm font-medium rounded-md">Get started <ArrowRight className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </header>

      {/* Hero — full-width photo dominant */}
      <section className="relative">
        <div className="relative h-[85vh] min-h-[600px] w-full overflow-hidden bg-ink-950">
          <img
            src="https://images.pexels.com/photos/7551667/pexels-photo-7551667.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop"
            alt="Caregiver with client"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-ink-950/40" />

          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 flex flex-col justify-end pb-16 lg:pb-24">
            <div className="max-w-3xl">
              <p className="text-xs font-medium text-white/60 uppercase tracking-[0.2em] mb-5 animate-fade-in-down">
                Recruitment software for care agencies
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tightest mb-6 text-balance animate-fade-in-up">
                Your own branded recruitment page, in minutes.
              </h1>
              <p className="text-lg lg:text-xl text-white/70 leading-relaxed max-w-xl mb-8 animate-fade-in-up delay-200">
                Create a custom application form, share your unique link everywhere, and manage
                all your candidates from one clean dashboard.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-300">
                <button
                  onClick={onGetStarted}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white hover:bg-ink-100 text-ink-900 font-semibold rounded-md transition-colors"
                >
                  Get started — it takes 2 minutes
                  <ArrowRight className="w-4 h-4" />
                </button>
                <p className="text-sm text-white/50 self-center">$50/month — cancel anytime</p>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8 animate-fade-in-up delay-500">
                {['30,000+ users', 'GDPR compliant', 'Cancel anytime'].map((text) => (
                  <div key={text} className="flex items-center gap-1.5">
                    <Check className="w-4 h-4 text-white/70" />
                    <span className="text-sm text-white/60">{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding — the problem */}
      <section className="py-24 lg:py-32 bg-white border-b border-ink-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10">
          <p className="text-xs font-medium text-ink-400 uppercase tracking-[0.2em] mb-6">
            The problem
          </p>
          <h2 className="text-3xl lg:text-5xl font-bold text-ink-900 leading-[1.1] tracking-tighter mb-8 text-balance">
            Hiring care workers shouldn't mean drowning in emails, spreadsheets, and lost CVs.
          </h2>
          <p className="text-lg text-ink-500 leading-relaxed max-w-2xl">
            Most care agencies still manage recruitment the same way they did ten years ago —
            forwarding CVs, pasting questions into email replies, and hoping nothing falls through
            the cracks. It's slow, it's messy, and when you're hiring for roles that matter, it's
            not good enough.
          </p>
        </div>
      </section>

      {/* The idea — how it works */}
      <section className="py-24 lg:py-32 bg-ink-50 border-b border-ink-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
          <p className="text-xs font-medium text-ink-400 uppercase tracking-[0.2em] mb-6">
            How it works
          </p>
          <h2 className="text-3xl lg:text-5xl font-bold text-ink-900 leading-[1.1] tracking-tighter mb-16 text-balance">
            Up and running in three steps. No setup fees.
          </h2>

          <div className="grid md:grid-cols-3 gap-12 lg:gap-16">
            {[
              { step: '01', title: 'Create your account', desc: 'Sign up and your agency profile + unique application link are ready instantly.' },
              { step: '02', title: 'Build your form', desc: 'Add the screening questions you care about. Drag to reorder, toggle required fields.' },
              { step: '03', title: 'Share your link', desc: 'Post it on Facebook, LinkedIn, Indeed, your website — anywhere candidates are looking.' },
            ].map(({ step, title, desc }) => (
              <div key={step} className="border-t border-ink-300 pt-6">
                <p className="text-sm font-mono text-ink-400 mb-3">{step}</p>
                <h3 className="text-xl font-semibold text-ink-900 mb-2">{title}</h3>
                <p className="text-sm text-ink-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features — compact grid */}
      <section className="py-24 lg:py-32 bg-white border-b border-ink-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
          <p className="text-xs font-medium text-ink-400 uppercase tracking-[0.2em] mb-6">
            What's included
          </p>
          <h2 className="text-3xl lg:text-5xl font-bold text-ink-900 leading-[1.1] tracking-tighter mb-16 text-balance">
            Everything you need to hire better.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-12">
            {FEATURES.map(({ icon: Icon, title, description }) => (
              <div key={title} className="border-t border-ink-200 pt-5">
                <Icon className="w-5 h-5 text-ink-700 mb-4" strokeWidth={1.5} />
                <h3 className="font-semibold text-ink-900 mb-2">{title}</h3>
                <p className="text-sm text-ink-500 leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Human connection — the memorable section */}
      <section className="py-32 lg:py-48 bg-ink-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
          <Heart className="w-8 h-8 text-white/30 mx-auto mb-10" strokeWidth={1} />
          <p className="text-2xl lg:text-4xl font-serif text-white/90 leading-[1.4] tracking-tight text-balance">
            "We built CareHire because we watched our own family struggle to hire good carers.
            The CVs were there — buried in inboxes, lost in forwarded threads, printed and filed
            in folders nobody opened. The people were ready. The system wasn't."
          </p>
          <p className="text-sm text-white/40 mt-10 uppercase tracking-[0.15em]">
            The reason CareHire exists
          </p>
        </div>
      </section>

      {/* Proof — why agencies switch */}
      <section className="py-24 lg:py-32 bg-white border-b border-ink-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
          <p className="text-xs font-medium text-ink-400 uppercase tracking-[0.2em] mb-6">
            Why agencies switch
          </p>
          <h2 className="text-3xl lg:text-5xl font-bold text-ink-900 leading-[1.1] tracking-tighter mb-16 text-balance">
            Hire the smart way — not the hard way.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {[
              { icon: Shield, title: 'Trusted by 30,000+', desc: "Care agencies across the USA and Canada rely on CareHire to handle their most important hires. Bank-level encryption and compliance keep every candidate's data safe." },
              { icon: Users, title: 'Less stress', desc: "No more spreadsheets, lost CVs in inboxes, or sticky notes. One clean dashboard, one source of truth — your hiring process finally feels under control." },
              { icon: Zap, title: 'Fewer notifications', desc: "Tame the noise. Get one clear alert when a new application arrives, not a flood of emails for every little change. You stay informed without being interrupted." },
              { icon: BarChart3, title: 'Saves you time', desc: "Set up your application page in under 10 minutes. Candidates come to you, sorted and screened automatically. Reclaim hours every week." },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="border-t border-ink-200 pt-5">
                <Icon className="w-5 h-5 text-ink-700 mb-4" strokeWidth={1.5} />
                <h3 className="font-semibold text-ink-900 mb-2">{title}</h3>
                <p className="text-sm text-ink-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who we serve */}
      <section className="py-24 lg:py-32 bg-ink-50 border-b border-ink-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
          <p className="text-xs font-medium text-ink-400 uppercase tracking-[0.2em] mb-6">
            Who we serve
          </p>
          <h2 className="text-3xl lg:text-5xl font-bold text-ink-900 leading-[1.1] tracking-tighter mb-4 text-balance">
            Built for home care agencies — operating worldwide.
          </h2>
          <p className="text-lg text-ink-500 leading-relaxed max-w-2xl mb-16">
            Our customers are mostly home care and recruitment agencies across the USA and Canada —
            but CareHire works for care agencies anywhere in the world.
          </p>
          <div className="grid sm:grid-cols-3 gap-8 mb-10">
            {[
              { title: 'United States', desc: 'Home care, home health, and caregiver staffing agencies from coast to coast.' },
              { title: 'Canada', desc: 'Personal support worker (PSW) and home care agencies across every province.' },
              { title: 'Global', desc: 'Care and recruitment agencies worldwide use CareHire to manage their hiring.' },
            ].map(({ title, desc }) => (
              <div key={title} className="border-t border-ink-300 pt-5">
                <Globe className="w-5 h-5 text-ink-600 mb-4" strokeWidth={1.5} />
                <h3 className="font-semibold text-ink-900 mb-2">{title}</h3>
                <p className="text-sm text-ink-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="border border-ink-200 bg-white rounded-md p-6">
            <p className="text-sm text-ink-600">
              <span className="font-semibold text-ink-900">Mostly USA &amp; Canada — but globally available.</span>{' '}
              CareHire is built primarily for home care agencies in the United States and Canada, and we proudly serve care and recruitment agencies operating around the world.
            </p>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section id="reviews" className="py-24 lg:py-32 bg-white border-b border-ink-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-ink-900 fill-ink-900" />)}
            </div>
            <span className="text-sm font-semibold text-ink-700">4.9/5</span>
          </div>
          <p className="text-xs font-medium text-ink-400 uppercase tracking-[0.2em] mb-4">
            Reviews
          </p>
          <h2 className="text-3xl lg:text-5xl font-bold text-ink-900 leading-[1.1] tracking-tighter mb-16 text-balance">
            Agencies love it.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-12">
            {TESTIMONIALS.map(({ quote, author, role, photo }) => (
              <div key={author} className="border-t border-ink-200 pt-6">
                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 text-ink-900 fill-ink-900" />)}
                </div>
                <p className="text-ink-700 leading-relaxed mb-6 font-serif text-lg">"{quote}"</p>
                <div className="flex items-center gap-3">
                  <img src={photo} alt={author} className="w-9 h-9 rounded-full object-cover grayscale" />
                  <div>
                    <p className="text-ink-900 text-sm font-semibold">{author}</p>
                    <p className="text-ink-400 text-xs">{role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 lg:py-32 bg-ink-50 border-b border-ink-100">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-10">
          <p className="text-xs font-medium text-ink-400 uppercase tracking-[0.2em] mb-6 text-center">
            Pricing
          </p>
          <h2 className="text-3xl lg:text-5xl font-bold text-ink-900 leading-[1.1] tracking-tighter mb-4 text-center text-balance">
            Simple, transparent pricing.
          </h2>
          <p className="text-ink-500 text-center mb-16">One plan. Everything included. No surprises.</p>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Monthly */}
            <div className="bg-white border border-ink-200 rounded-lg p-10 editorial-shadow flex flex-col">
              <span className="text-xs font-semibold text-ink-500 uppercase tracking-[0.15em]">Full Access</span>

              <div className="flex items-baseline gap-1 mt-6 mb-1">
                <span className="text-5xl font-bold text-ink-900 tracking-tightest">$50</span>
                <span className="text-ink-400 text-lg">/month</span>
              </div>
              <p className="text-ink-500 text-sm mb-8">per agency — billed monthly, cancel anytime</p>

              <ul className="space-y-3 mb-8 flex-1">
                {[
                  'Unlimited applications',
                  'Custom screening form builder',
                  'Drag-and-drop question reordering',
                  'Full applicant dashboard',
                  'CV upload & storage',
                  'Status pipeline management',
                  'Email notifications',
                  'Shareable link for any platform',
                  'GDPR-compliant & secure',
                  'Cancel anytime',
                ].map((feature) => (
                  <li key={feature} className="flex items-center gap-3 text-sm text-ink-700">
                    <Check className="w-4 h-4 text-ink-900 flex-shrink-0" strokeWidth={2} />
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                onClick={onGetStarted}
                className="w-full py-3.5 bg-ink-900 hover:bg-ink-800 text-white font-semibold rounded-md transition-colors"
              >
                Start your subscription
              </button>
              <p className="text-xs text-ink-400 text-center mt-4">$50/month — cancel anytime</p>
              <div className="mt-4 flex items-start gap-2 p-2.5 rounded-md bg-green-50 border border-green-100">
                <Shield className="w-3.5 h-3.5 text-green-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="text-xs text-green-700 leading-relaxed">
                  <span className="font-semibold">7-day money-back guarantee.</span> Not happy? Email{' '}
                  <a href="mailto:carehire@outlook.com" className="font-semibold underline">carehire@outlook.com</a>{' '}
                  within a week for a full refund.
                </p>
              </div>
            </div>

            {/* Yearly */}
            <div className="bg-white border-2 border-ink-900 rounded-lg p-10 editorial-shadow flex flex-col relative">
              <span className="absolute top-0 right-0 -mt-px -mr-px bg-ink-900 text-white text-xs font-bold px-4 py-1.5 rounded-bl-lg rounded-tr-lg uppercase tracking-wide">
                Most Popular
              </span>
              <span className="text-xs font-semibold text-ink-500 uppercase tracking-[0.15em]">Full Access</span>

              <div className="flex items-baseline gap-1 mt-6 mb-1">
                <span className="text-5xl font-bold text-ink-900 tracking-tightest">$480</span>
                <span className="text-ink-400 text-lg">/year</span>
              </div>
              <p className="text-ink-500 text-sm mb-2">per agency — billed annually, cancel anytime</p>
              <p className="text-sm font-semibold text-ink-900 mb-6">
                Save $120 a year <span className="text-ink-400 font-normal">(20% off)</span>
              </p>

              <ul className="space-y-3 mb-8 flex-1">
                {[
                  'Unlimited applications',
                  'Custom screening form builder',
                  'Drag-and-drop question reordering',
                  'Full applicant dashboard',
                  'CV upload & storage',
                  'Status pipeline management',
                  'Email notifications',
                  'Shareable link for any platform',
                  'GDPR-compliant & secure',
                  'Cancel anytime',
                ].map((feature) => (
                  <li key={feature} className="flex items-center gap-3 text-sm text-ink-700">
                    <Check className="w-4 h-4 text-ink-900 flex-shrink-0" strokeWidth={2} />
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                onClick={onGetStarted}
                className="w-full py-3.5 bg-ink-900 hover:bg-ink-800 text-white font-semibold rounded-md transition-colors"
              >
                Start your subscription
              </button>
              <p className="text-xs text-ink-400 text-center mt-4">$480/year — cancel anytime</p>
              <div className="mt-4 flex items-start gap-2 p-2.5 rounded-md bg-green-50 border border-green-100">
                <Shield className="w-3.5 h-3.5 text-green-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="text-xs text-green-700 leading-relaxed">
                  <span className="font-semibold">7-day money-back guarantee.</span> Not happy? Email{' '}
                  <a href="mailto:carehire@outlook.com" className="font-semibold underline">carehire@outlook.com</a>{' '}
                  within a week for a full refund.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Action — final CTA */}
      <section className="py-32 lg:py-48 bg-ink-950">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-4xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tightest mb-6 text-balance">
            Ready to hire smarter?
          </h2>
          <p className="text-lg text-white/50 mb-10">Join over 30,000 users already using CareHire.</p>
          <button
            onClick={onGetStarted}
            className="inline-flex items-center gap-2 px-8 py-4 bg-white hover:bg-ink-100 text-ink-900 font-semibold rounded-md transition-colors"
          >
            Create your account
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-ink-200 py-14 bg-ink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <span className="font-bold text-ink-900 text-sm">CareHire</span>
              </div>
              <p className="text-xs text-ink-500 leading-relaxed mb-3">Smart recruitment software for care agencies.</p>
              <a href="mailto:carehire@outlook.com" className="text-xs text-ink-600 hover:text-ink-900 transition-colors inline-flex items-center gap-1">
                <Mail className="w-3 h-3" /> carehire@outlook.com
              </a>
              <p className="text-xs text-ink-400 mt-3">© {new Date().getFullYear()} CareHire Ltd</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-ink-700 uppercase tracking-[0.15em] mb-4">Product</p>
              <div className="space-y-2.5">
                <button onClick={() => scrollTo('pricing')} className="block text-xs text-ink-500 hover:text-ink-900 transition-colors">Pricing</button>
                <button onClick={() => scrollTo('reviews')} className="block text-xs text-ink-500 hover:text-ink-900 transition-colors">Reviews</button>
                <button onClick={onGetStarted} className="block text-xs text-ink-500 hover:text-ink-900 transition-colors">Get started</button>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-ink-700 uppercase tracking-[0.15em] mb-4">Company</p>
              <div className="space-y-2.5">
                <button onClick={() => onNavigateInfo('about')} className="block text-xs text-ink-500 hover:text-ink-900 transition-colors">About</button>
                <button onClick={() => onNavigateInfo('blog')} className="block text-xs text-ink-500 hover:text-ink-900 transition-colors">Blog</button>
                <button onClick={() => onNavigateInfo('faq')} className="block text-xs text-ink-500 hover:text-ink-900 transition-colors">FAQ</button>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-ink-700 uppercase tracking-[0.15em] mb-4">Legal</p>
              <div className="space-y-2.5">
                <button onClick={() => onNavigateLegal('privacy')} className="block text-xs text-ink-500 hover:text-ink-900 transition-colors">Privacy Policy</button>
                <button onClick={() => onNavigateLegal('terms')} className="block text-xs text-ink-500 hover:text-ink-900 transition-colors">Terms of Service</button>
                <button onClick={() => onNavigateLegal('cookies')} className="block text-xs text-ink-500 hover:text-ink-900 transition-colors">Cookie Policy</button>
                <button onClick={() => onNavigateLegal('gdpr')} className="block text-xs text-ink-500 hover:text-ink-900 transition-colors">GDPR Compliance</button>
                <button onClick={() => onNavigateLegal('acceptable-use')} className="block text-xs text-ink-500 hover:text-ink-900 transition-colors">Acceptable Use</button>
                <button onClick={() => onNavigateLegal('disclaimer')} className="block text-xs text-ink-500 hover:text-ink-900 transition-colors">Disclaimer</button>
                <button onClick={() => onNavigateLegal('copyright')} className="block text-xs text-ink-500 hover:text-ink-900 transition-colors">Copyright Notice</button>
                <button onClick={() => onNavigateLegal('data-retention')} className="block text-xs text-ink-500 hover:text-ink-900 transition-colors">Data Retention</button>
                <button onClick={() => onNavigateLegal('dpa')} className="block text-xs text-ink-500 hover:text-ink-900 transition-colors">Data Processing</button>
                <button onClick={onNavigateAdmin} className="block text-xs text-ink-400 hover:text-ink-600 transition-colors">Admin</button>
              </div>
            </div>
          </div>
          <div className="pt-8 border-t border-ink-200 text-center">
            <p className="text-xs text-ink-400">CareHire Ltd · Registered in England and Wales · Company No. 12345678 · VAT GB 123 4567 89</p>
          </div>
        </div>
      </footer>

      {/* Report a Problem modal */}
      {reportOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setReportOpen(false)}>
          <div className="absolute inset-0 bg-ink-950/50 backdrop-blur-sm" />
          <div
            className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6 sm:p-8 animate-fade-in-up"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setReportOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-ink-400 hover:text-ink-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <Flag className="w-5 h-5 text-red-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-ink-900">Report a Website Problem</h3>
            </div>
            <p className="text-base text-ink-700 leading-relaxed mb-6">
              Email us here at{' '}
              <a
                href="mailto:carehire@outlook.com?subject=Report%20a%20Website%20Problem"
                className="font-semibold text-ink-900 underline underline-offset-2 hover:text-ink-600 transition-colors"
              >
                carehire@outlook.com
              </a>
            </p>
            <button
              onClick={copyEmail}
              className="w-full px-5 py-3 bg-ink-900 hover:bg-ink-800 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {copied ? 'Email address copied!' : 'Copy email address'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
